import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {GridBasis} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridBasis';
import {GridEvent, GridEventType} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridEvent';
import {IMutableGridModel, RowIndex} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {Perspectives, ScreenRegion} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/layout/Perspective';
import {
    IPanelDatum, Panel, PanelHeader,
    PanelKind
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {WSPIconName, WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Badge, BadgeProps} from '@mintdata-wsp/wsp/ui/components/badge/Badge';
import {IconCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/IconCell';
import {LetterCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/LetterCell';
import {TextCell} from '@mintdata-wsp/wsp/ui/components/grid/cells/TextCell';
import {GridRowSelectionChanged} from '@mintdata-wsp/wsp/ui/components/grid/GridEvent';
import {GridListColumn, GridModels} from '@mintdata-wsp/wsp/ui/components/grid/GridModels';
import {ProblemsPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/errors/ProblemsPanel';
import {action, IReactionDisposer, reaction} from 'mobx';
import * as React from 'react';
import {
    FlashBlockType,
    Flow,
    FlowBlockId,
    FlowValidationError,
    FlowValidationErrorType,
    MessageSeverity,
    SimulationMessage
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';

export type OnProblemRowSelected = () => (void);

export class ProblemsPanelController
{
    private _rowIndexToOnRowSelected:Map<RowIndex, OnProblemRowSelected>;
    private _disposers:IReactionDisposer[] = [];

    public init():void
    {
        this._rowIndexToOnRowSelected = new Map();

        this._setupGrid();
        this._listenForOpenFlow();
        this._listenForFlowSimulationMessages();
    }

    public getGridModel():IMutableGridModel
    {
        return rootState.ui.visualDesigner.workbench.panels.errors.errorsList;
    }

    public clearAllMessages():void
    {
        this._rowIndexToOnRowSelected = new Map();

        const gridModel = this.getGridModel();
        gridModel.clearDataRows();
    }

    public hidePanel():void
    {
        this._closeProblemsPanel();
    }

    public onGridEvent = (e:GridEvent):void =>
    {
        if( e.type === GridEventType.RowSelectionChanged )
        {
            const evt = (e as GridRowSelectionChanged);
            const {isRowSelected, rowIndex} = evt;

            if( isRowSelected )
            {
                const onRowSelected:OnProblemRowSelected = this._rowIndexToOnRowSelected.get(rowIndex);

                if( onRowSelected && typeof onRowSelected === 'function' )
                {
                    onRowSelected();
                }
            }
        }
    };

    @action
    public ensurePanel():Panel
    {
        const panelHeader = PanelHeader.withCloseableTitle('Problems');

        const datum = new ErrorsPanelDatum();
        const element = React.createElement(ProblemsPanel, {});

        const flowTab = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);
        const controller = controllers.ui.panels.general;

        return controller.getOrCreatePanel(flowTab,
            ScreenRegion.South,
            PanelKind.FlowProblems,
            panelHeader,
            datum,
            element);
    }

    @action
    public selectPanel()
    {
        const controller = controllers.ui.panels.general;

        const flowErrorsPanel = this.ensurePanel();
        const style = {}; //marginRight:'3px'};
        const numErrors = this.getGridModel().getBody().getNumRows();

        flowErrorsPanel.header.icon = this._newBadge(MDIntent.Default, '' + numErrors, style);

        controller.selectPanel(flowErrorsPanel.id);
    }

    public static newProblemRowSelectedFn(blockId:FlowBlockId,
                                          error?:FlowValidationError,
                                          flashType?:FlashBlockType):OnProblemRowSelected
    {
        return () =>
        {
            const {
                flowCanvas,
                flowPropertiesPanel,
                flowSelection,
                flowTopBar
            } = controllers.ui;

            const block = flowCanvas.findBlock(blockId);

            if( block )
            {
                flowSelection.setSingleBlockSelection(block, false);
                flowTopBar.setPropertiesPanelVisibility(true);

                if( error && error.type === FlowValidationErrorType.Property )
                {
                    flowPropertiesPanel.requestFocusProperty(block.id, error.flowPropertyName);
                }

                if( flashType )
                {
                    flowSelection.requestFlashBlock(block.id, flashType);
                }
            }
        };
    }

    @action
    private _setupGrid()
    {
        const columns = [
            new GridListColumn('logLevel', '', GridBasis.fixed(25)),
            new GridListColumn('message', 'Message', GridBasis.stretch(1)),
            new GridListColumn('source', 'Source', GridBasis.fixed(20))
        ];

        const gridModel = GridModels.newForList(columns, false, true, false);

        gridModel.addListener(this.onGridEvent);
        this._setGridModel(gridModel);
    }

    private _listenForOpenFlow()
    {
        const disposer = reaction(
            () => controllers.ui.flow.getOpenFlow(),
            (flow:Flow) => this._onFlowChanged(flow)
        );

        this._disposers.push(disposer);
    }

    private _listenForFlowSimulationMessages()
    {
        const disposer = reaction(
            () =>
            {
                const openFlow = controllers.ui.flow.getOpenFlow();

                if( openFlow )
                {
                    const simulation = controllers.ui.flowSimulation.getForFlowEditor(openFlow.id);

                    if( simulation )
                    {
                        return simulation.messages.length;
                    }
                }

                return 0;

            },
            (messageCount) => this._onFlowSimulationMessagesChanged(messageCount)
        );

        this._disposers.push(disposer);
    }

    @action
    private _onFlowChanged(flow:Flow | null)
    {
        this.clearAllMessages();
        this.hidePanel();

        if( flow )
        {
            const simulation = controllers.ui.flowSimulation.getForFlowEditor(flow.id);
            const messages = simulation ? simulation.messages.filter(this._problemsFilter) : [];

            if( messages.length )
            {
                for( const message of messages )
                {
                    const onProblemRowSelected = this._getProblemRowSelectedHandler(message);

                    this._logProblem(message.message, message.severity, onProblemRowSelected);
                }

                this.ensurePanel();
            }
        }
    };

    @action
    private _onFlowSimulationMessagesChanged(messageCount:number):void
    {
        // todo: Not effective. Messages diff should be handled
        this.clearAllMessages();

        const openFlow = controllers.ui.flow.getOpenFlow();

        if( !openFlow )
        {
            this.hidePanel();
            return;
        }

        const simulation = controllers.ui.flowSimulation.getForFlowEditor(openFlow.id);
        const messages = simulation ? simulation.messages.filter(this._problemsFilter) : [];

        if( messages.length )
        {
            for( const message of messages )
            {
                const onProblemRowSelected = this._getProblemRowSelectedHandler(message);

                this._logProblem(message.message, message.severity, onProblemRowSelected);
            }

            this.ensurePanel();
        }
        else
        {
            this.hidePanel();
        }
    };

    private _getProblemRowSelectedHandler(message:SimulationMessage):OnProblemRowSelected
    {
        if( message.isFlowError )
        {
            const {flowErrorRef} = message;
            return ProblemsPanelController.newProblemRowSelectedFn(flowErrorRef.flowBlockId, flowErrorRef);
        }
        else if( message.isCloudFlowError )
        {
            const {scopeIds} = message.cloudFlowErrorRef;

            if( scopeIds && scopeIds.boxId )
            {
                const flowBlockId = scopeIds.boxId as FlowBlockId;
                return ProblemsPanelController.newProblemRowSelectedFn(flowBlockId);
            }
        }

        return null;
    }

    private _problemsFilter = (message:SimulationMessage):boolean =>
    {
        return [MessageSeverity.Error, MessageSeverity.Warn].indexOf(message.severity) !== -1;
    };

    private _logProblem(message:string,
                        severity:MessageSeverity,
                        onProblemRowSelected:OnProblemRowSelected = null)
    {
        const iconName = severity === MessageSeverity.Warn ?
            WSPIcons.visualDesigner.panels.console.Warning :
            WSPIcons.visualDesigner.panels.console.Error;

        const letterCell = new LetterCell('F', 'Flow');

        this._appendRow(iconName, message, letterCell);

        if( onProblemRowSelected && typeof onProblemRowSelected === 'function' )
        {
            this._rowIndexToOnRowSelected.set(this._getNumRows() - 1, onProblemRowSelected);
        }

        this.ensurePanel();
    }

    private _getNumRows():number
    {
        return this.getGridModel().getBodyWithColumnHeaders().getNumRows();
    }

    private _appendRow(iconName:WSPIconName,
                       message:string,
                       letterCell:LetterCell)
    {
        const row = [
            new IconCell(iconName, WSPIconSize.medium16),
            new TextCell(message),
            letterCell
        ];

        const gridModel = this.getGridModel();
        gridModel.appendDataRow(row);
    }

    private _setGridModel(gridModel:IMutableGridModel):void
    {
        rootState.ui.visualDesigner.workbench.panels.errors.errorsList = gridModel;
    }

    private _closeProblemsPanel()
    {
        const flowTab = controllers.ui.perspective.getPerspective(Perspectives.FLOW_TAB);
        const controller = controllers.ui.panels.general;

        controller.removeAnyUnpinnedOfKind(flowTab, PanelKind.FlowProblems);
    }

    private _newBadge(intent:MDIntent, label:string, style?:any):React.ReactElement<BadgeProps>
    {
        const props:BadgeProps = {} as BadgeProps;
        props.intent = intent;
        props.label = label;
        props.style = style;

        return React.createElement(Badge, props);
    }
}

export class ErrorsPanelDatum implements IPanelDatum
{
    private readonly datumKind = 'ErrorsPanelDatum';

    isEqualTo(other:ErrorsPanelDatum):boolean
    {
        return other && other.datumKind === this.datumKind;
    }
}
